/**
 * Invoice mutation hooks using generic factory
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { invoiceApi } from '@/lib/api/invoices.api';
import { queryKeys } from '../query-keys';
import { createResourceMutations } from './createResourceMutations';
import { toast } from 'sonner';
import type { Invoice, CreateInvoice, UpdateInvoiceInput } from '@scrambl/shared';

const mutations = createResourceMutations<Invoice, CreateInvoice, UpdateInvoiceInput>({
  resourceName: 'Invoice',
  resourceNameSingular: 'invoice',
  queryKeyFactory: queryKeys.invoices,
  api: {
    create: (data: CreateInvoice) => invoiceApi.createInvoice(data),
    update: (id: string, data: UpdateInvoiceInput) => invoiceApi.updateInvoice(id, data),
    updateField: (id: string, field: string, value: unknown) => {
      const updateData = { [field]: value } as UpdateInvoiceInput;
      return invoiceApi.updateInvoice(id, updateData);
    },
    delete: (id: string) => invoiceApi.deleteInvoice(id),
  },
  enableRealtime: false,
});

export const useCreateInvoiceMutation = mutations.useCreateMutation;
export const useUpdateInvoiceMutation = mutations.useUpdateMutation;
export const useUpdateInvoiceFieldMutation = mutations.useUpdateFieldMutation!;
export const useDeleteInvoiceMutation = mutations.useDeleteMutation;

/**
 * Bulk delete invoices mutation hook
 */
export function useBulkDeleteInvoicesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => invoiceApi.bulkDeleteInvoices(ids),
    onSuccess: (result) => {
      toast.success(`${result.deletedCount} invoice${result.deletedCount === 1 ? '' : 's'} deleted`);
      // Invalidate invoices list
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.lists() });
    },
    onError: (error) => {
      toast.error('Failed to delete invoices');
      console.error('Bulk delete invoices error:', error);
    },
  });
}
